/**
 * Production-safe logging utility
 * Suppresses debug logs in production while keeping errors and warnings
 */

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || "development"
const IS_PRODUCTION = ENVIRONMENT === "production"

// Store original console methods
const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
}

/**
 * Override global console to suppress debug logs in production
 * This affects all console calls throughout the app automatically
 */
export function setupProductionConsole() {
  if (IS_PRODUCTION) {
    // Suppress debug logs in production
    console.log = () => {}
    console.info = () => {}
    console.debug = () => {}

    // Keep warnings and errors, but use original console methods
    // (These will still show in browser console for debugging)
    console.warn = originalConsole.warn
    console.error = originalConsole.error
  }
  // In development, keep all console methods as-is
}

/**
 * Restore original console methods (useful for testing)
 */
export function restoreConsole() {
  console.log = originalConsole.log
  console.info = originalConsole.info
  console.debug = originalConsole.debug
  console.warn = originalConsole.warn
  console.error = originalConsole.error
}

/**
 * Logger interface matching console API
 * Use this for explicit logging if you want more control
 */
export const logger = {
  /**
   * Debug log - suppressed in production
   */
  log: (...args: unknown[]) => {
    if (!IS_PRODUCTION) {
      originalConsole.log(...args)
    }
  },

  /**
   * Info log - suppressed in production
   */
  info: (...args: unknown[]) => {
    if (!IS_PRODUCTION) {
      originalConsole.info(...args)
    }
  },

  /**
   * Debug log - suppressed in production
   */
  debug: (...args: unknown[]) => {
    if (!IS_PRODUCTION) {
      originalConsole.debug(...args)
    }
  },

  /**
   * Warning log - always shown, consider sending to monitoring service
   */
  warn: (...args: unknown[]) => {
    originalConsole.warn(...args)
    // TODO: Send to error tracking service in production
  },

  /**
   * Error log - always shown, should be sent to error tracking service
   */
  error: (...args: unknown[]) => {
    originalConsole.error(...args)
    // TODO: Send to error tracking service in production
  },
}

/**
 * Export default logger for convenience
 */
export default logger
